import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Droplets, Clock, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OwnerWateringView = () => {
  const [rooms, setRooms] = useState([]);
  const [wateringLogs, setWateringLogs] = useState([]);
  const [roomCycles, setRoomCycles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      
      const [roomsRes, logsRes, cyclesRes] = await Promise.all([
        axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/watering/logs?date=${today}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/room-cycles?status=active`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setRooms(roomsRes.data);
      // Handle both array and object response formats
      const logsData = logsRes.data?.logs || logsRes.data || [];
      setWateringLogs(logsData);
      // Handle both array and object with 'cycles' key response formats
      const cyclesData = Array.isArray(cyclesRes.data) ? cyclesRes.data : (cyclesRes.data.cycles || []);
      setRoomCycles(cyclesData);
    } catch (error) {
      console.error('Watering data fetch error:', error);
      // Don't show toast for minor errors - data might still be partially available
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  // Determine which rooms need watering based on their current phase
  const getRoomWateringStatus = (room) => {
    const cycle = roomCycles.find(c => c.room_id === room.id);
    const todayLogs = wateringLogs.filter(log => log.room_id === room.id);
    
    // Rooms in certain phases need watering
    const phasesNeedingWater = ['spawn_run', 'casing', 'case_run', 'pinning', 'flush_1', 'flush_2', 'airing'];
    
    let needsWatering = false;
    let currentPhase = 'Idle';
    let currentDay = 0;
    
    if (cycle) {
      currentPhase = cycle.current_phase || 'Unknown';
      currentDay = cycle.current_day || 0;
      needsWatering = phasesNeedingWater.includes(currentPhase);
    }
    
    // Get today's watering log if exists
    const todayLog = todayLogs.length > 0 ? todayLogs[todayLogs.length - 1] : null;
    
    return {
      needsWatering,
      isWatered: todayLog !== null,
      wateringLog: todayLog,
      currentPhase,
      currentDay,
      hasActiveCycle: !!cycle
    };
  };

  const formatPhase = (phase) => {
    const phaseNames = {
      'spawning': 'Spawning',
      'spawn_run': 'Spawn Run',
      'casing': 'Casing',
      'case_run': 'Case Run',
      'pinning': 'Pinning',
      'flush_1': 'Flush 1',
      'flush_2': 'Flush 2',
      'airing': 'Airing',
      'rest': 'Rest',
      'idle': 'Idle'
    };
    return phaseNames[phase] || phase;
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading watering status...</div>;
  }

  // Categorize rooms
  const roomsNeedingWater = [];
  const roomsWatered = [];
  const roomsNoWaterNeeded = [];

  rooms.forEach(room => {
    const status = getRoomWateringStatus(room);
    const roomData = { ...room, ...status };
    
    if (status.isWatered) {
      roomsWatered.push(roomData);
    } else if (status.needsWatering) {
      roomsNeedingWater.push(roomData);
    } else {
      roomsNoWaterNeeded.push(roomData);
    }
  });

  return (
    <div className="space-y-6" data-testid="owner-watering-view">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Needs Watering</p>
                <p className="text-3xl font-bold text-red-800">{roomsNeedingWater.length}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Watered Today</p>
                <p className="text-3xl font-bold text-green-800">{roomsWatered.length}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">No Watering Needed</p>
                <p className="text-3xl font-bold text-gray-800">{roomsNoWaterNeeded.length}</p>
              </div>
              <Droplets className="w-10 h-10 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rooms Needing Water - Priority Section */}
      {roomsNeedingWater.length > 0 && (
        <Card className="border-red-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-5 h-5" />
              Rooms Needing Watering Today ({roomsNeedingWater.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {roomsNeedingWater.map(room => (
                <div 
                  key={room.id} 
                  className="p-4 border-2 border-red-300 rounded-lg bg-red-50"
                  data-testid={`room-needs-water-${room.room_number}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-lg">{room.name}</span>
                    <Badge className="bg-red-100 text-red-800">Pending</Badge>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Phase: <span className="font-medium">{formatPhase(room.currentPhase)}</span></p>
                    <p>Day: <span className="font-medium">{room.currentDay}</span></p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rooms Already Watered - With Timestamps */}
      {roomsWatered.length > 0 && (
        <Card className="border-green-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <CheckCircle className="w-5 h-5" />
              Rooms Watered Today ({roomsWatered.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {roomsWatered.map(room => (
                <div 
                  key={room.id} 
                  className="p-4 border-2 border-green-300 rounded-lg bg-green-50"
                  data-testid={`room-watered-${room.room_number}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-lg">{room.name}</span>
                    <Badge className="bg-green-100 text-green-800">Done</Badge>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Phase: <span className="font-medium">{formatPhase(room.currentPhase)}</span></p>
                    <p>Day: <span className="font-medium">{room.currentDay}</span></p>
                    {room.wateringLog && (
                      <>
                        <div className="pt-2 border-t border-green-200 mt-2">
                          <p className="flex items-center gap-1 text-green-700">
                            <Droplets className="w-3 h-3" />
                            <span className="font-medium">{room.wateringLog.water_given_liters} L</span>
                          </p>
                          <p className="flex items-center gap-1 text-green-600 text-xs">
                            <Clock className="w-3 h-3" />
                            <span>
                              {room.wateringLog.log_date 
                                ? format(new Date(room.wateringLog.log_date), 'h:mm a')
                                : 'Time not recorded'}
                            </span>
                          </p>
                          {room.wateringLog.logged_by_name && (
                            <p className="text-xs text-green-600 mt-1">
                              By: {room.wateringLog.logged_by_name}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rooms Not Needing Water */}
      {roomsNoWaterNeeded.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-600">
              <Droplets className="w-5 h-5" />
              Rooms Not Requiring Watering ({roomsNoWaterNeeded.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
              {roomsNoWaterNeeded.map(room => (
                <div 
                  key={room.id} 
                  className="p-3 border rounded-lg bg-gray-50 text-center"
                  data-testid={`room-no-water-${room.room_number}`}
                >
                  <span className="font-medium text-gray-700">{room.name}</span>
                  <p className="text-xs text-gray-500 mt-1">
                    {room.hasActiveCycle ? formatPhase(room.currentPhase) : 'Idle'}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Refresh Button */}
      <div className="flex justify-center">
        <Button 
          variant="outline" 
          onClick={fetchData}
          className="flex items-center gap-2"
          data-testid="refresh-watering-status"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh Status
        </Button>
      </div>
    </div>
  );
};

export default OwnerWateringView;
